/**
 * 개요 페이지 — **비교로 읽는 화면**.
 *
 * 이전 구성의 문제는 위젯이 각자 값을 하나씩 말할 뿐 서로 견주지 않았다는 것이다.
 * 숫자 하나만 크게 보여주면 "99% 면 좋은 거네" 로 끝난다. 이 페이지는 질문
 * 세 개를 순서대로 놓고, 각 질문마다 **비교 형태가 다른 그래프**를 쓴다:
 *
 *   ① 정의 격차 → 아령 (두 값의 차이)
 *   ② 순위      → 점 + 신뢰구간 (겹침 여부)
 *   ③ 신뢰      → 산점도 (두 축의 관계) + 분포 (평균 뒤의 퍼짐)
 *
 * 같은 형태를 반복하지 않는 이유는 각 질문이 다른 종류의 비교를 요구하기
 * 때문이다 (§8.6.2 형태 선택표).
 */

import { useState } from 'react'

import { EntityPicker } from '@/components/EntityPicker'
import { KpiStrip } from '@/components/KpiStrip'
import { Section } from '@/components/Section'
import { RANK_ENTITIES, type RankEntity } from '@/lib/types'
import { AvailabilityDistribution } from './overview/AvailabilityDistribution'
import { CoverageScatter } from './overview/CoverageScatter'
import { ModeGapDumbbell } from './overview/ModeGapDumbbell'
import { RankingCompare } from './overview/RankingCompare'
import './OverviewPage.css'

export function OverviewPage() {
  // **비교 축은 페이지가 하나만 갖는다.** 위젯마다 따로 두면 같은 화면에서
  // 그래프마다 다른 대상을 보게 되고, 세 그래프가 한 이야기를 못 한다.
  const [entity, setEntity] = useState<RankEntity>('maker')
  const axisLabel = RANK_ENTITIES.find((e) => e.key === entity)?.label ?? ''

  return (
    <div className="overview">
      <header className="overview__intro">
        <p className="overview__lead">
          이 페이지의 모든 그래프는 <strong>비교</strong>를 위한 것입니다. 값 하나만으로는 좋은지
          나쁜지 말할 수 없습니다 — 무엇과 견주는지, 그 차이가 관측량으로 설명되는지까지 함께
          봐야 합니다.
        </p>
        <div className="overview__axis">
          <span className="overview__axislabel" id="axis-label">
            비교 축
          </span>
          <EntityPicker value={entity} onChange={setEntity} label="비교 축" />
          <span className="overview__axishint">
            아래 세 그래프가 함께 <strong>{axisLabel}</strong> 기준으로 바뀝니다
          </span>
        </div>
      </header>

      <KpiStrip />

      <Section
        index={1}
        title="먼저, 무엇을 고장으로 셀 것인가"
        lead="같은 관측 데이터에 정의만 바꿔도 가동률이 80포인트 넘게 움직인다. 정의를 고르지 않은 가동률은 의미가 없다."
        layout="full"
      >
        <ModeGapDumbbell entity={entity} />
      </Section>

      <Section
        index={2}
        title="그 정의 위에서, 누가 더 잘 돌아가는가"
        lead="순위는 점 하나가 아니라 구간이다. 구간이 겹치면 '1위가 2위보다 낫다'고 말할 수 없다."
        layout="full"
      >
        <RankingCompare entity={entity} />
      </Section>

      <Section
        index={3}
        title="그리고, 그 숫자를 믿어도 되는가"
        lead="근거가 얇으면 좋은 값도 우연이다. 왼쪽은 근거의 두께, 오른쪽은 평균이 감춘 편차."
      >
        <CoverageScatter entity={entity} />
        <AvailabilityDistribution />
      </Section>
    </div>
  )
}
